// components/HeaderInfo.tsx

'use client';

import Link from 'next/link';
import HeaderClock from './HeaderClock';
import TrafficInfo, { type TrafficData } from './TrafficInfo';
import useSWR from 'swr';
import { useEffect, useMemo, useState } from 'react';
type ExchangeRates = {
  USD: string | null;
  EUR: string | null;
};

interface HeaderInfoProps {
  initialRates: ExchangeRates;
  initialTraffic: TrafficData;
}

type HeaderInfoResponse = {
  rates: ExchangeRates;
  traffic: TrafficData;
};

const fetcher = (url: string) => fetch(url).then((res) => res.json());
function ratesAria(USD: string | null, EUR: string | null) {
  const usdText = USD ?? 'нет данных';
  const eurText = EUR ?? 'нет данных';
  return `Курсы валют: Доллар ${usdText}, Евро ${eurText}`;
}

export default function HeaderInfo({ initialRates, initialTraffic }: HeaderInfoProps) {
  const [rates, setRates] = useState<ExchangeRates>(initialRates);
  const [traffic, setTraffic] = useState<TrafficData>(initialTraffic);

  const { data } = useSWR<HeaderInfoResponse>('/api/header-info', fetcher, {
    fallbackData: { rates: initialRates, traffic: initialTraffic },
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
  });

  useEffect(() => {
    if (initialRates.USD || initialRates.EUR) {
      setRates((prev) => ({
        USD: initialRates.USD ?? prev.USD,
        EUR: initialRates.EUR ?? prev.EUR,
      }));
    }

    if (initialTraffic.level !== null || initialTraffic.hint !== null) {
      setTraffic((prev) => ({
        level: initialTraffic.level ?? prev.level,
        hint: initialTraffic.hint ?? prev.hint,
        time: initialTraffic.time ?? prev.time ?? null,
      }));
    }
  }, [initialRates, initialTraffic]);

  useEffect(() => {
    if (!data) return;

    setRates((prev) => ({
      USD: data.rates?.USD ?? prev.USD,
      EUR: data.rates?.EUR ?? prev.EUR,
    }));

    setTraffic((prev) => ({
      level: data.traffic?.level ?? prev.level,
      hint: data.traffic?.hint ?? prev.hint,
      time: data.traffic?.time ?? prev.time ?? null,
    }));
  }, [data]);

  const usd = rates.USD ?? '—';
  const eur = rates.EUR ?? '—';
  const ratesLabel = useMemo(() => ratesAria(rates.USD, rates.EUR), [rates.USD, rates.EUR]);

  return (
    <div className="text-left text-white">
      <HeaderClock />

      <div className="mt-1 text-xs text-white md:text-sm space-y-1">
      <TrafficInfo initialTraffic={traffic} />

      <Link href="/finance" aria-label={ratesLabel}>
          <span>$ {usd}</span>
          <span className="mx-1 md:mx-2" aria-hidden="true">
            |
          </span>
          <span>€ {eur}</span>
        </Link>
      </div>
    </div>
  );
}
